import { useState, useEffect, useRef } from "react";
import { Timer, Play, Square, RotateCcw } from "lucide-react";

/**
 * ReadingTimer - Chronometre de lecture pour les eleves.
 * Mesure le temps de lecture et peut etre sauvegarde.
 */
export default function ReadingTimer({ onComplete, isReading }) {
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setSeconds((s) => s + 1);
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  // Auto-start when reading starts (audio play)
  useEffect(() => {
    if (isReading && !running && !finished) {
      setRunning(true);
    }
  }, [isReading, running, finished]);

  const formatTime = (totalSeconds) => {
    const min = Math.floor(totalSeconds / 60);
    const sec = totalSeconds % 60;
    return `${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const handleStart = () => {
    if (finished) {
      setSeconds(0);
      setFinished(false);
    }
    setRunning(true);
  };

  const handleStop = () => {
    setRunning(false);
    setFinished(true);
    clearInterval(intervalRef.current);
    if (onComplete) {
      onComplete(seconds);
    }
  };

  const handleReset = () => {
    setRunning(false);
    setFinished(false);
    setSeconds(0);
    clearInterval(intervalRef.current);
  };

  return (
    <div data-testid="reading-timer" className="flex items-center gap-2">
      {/* Timer display */}
      <div
        data-testid="timer-display"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-fredoka font-bold text-base tabular-nums ${
          running
            ? "bg-green-100 text-green-700"
            : finished
              ? "bg-orange-100 text-orange-700"
              : "bg-slate-100 text-slate-500"
        }`}
      >
        <Timer size={16} className={running ? "animate-pulse" : ""} />
        {formatTime(seconds)}
      </div>

      {/* Controls */}
      {!running && !finished && (
        <button
          data-testid="timer-start-btn"
          onClick={handleStart}
          className="p-2 rounded-xl bg-green-500 active:bg-green-600 text-white transition-all touch-target shadow-[0_2px_0_rgb(22,101,52)] btn-bouncy"
          title="Demarrer le chronometre"
        >
          <Play size={14} className="ml-0.5" />
        </button>
      )}

      {running && (
        <button
          data-testid="timer-stop-btn"
          onClick={handleStop}
          className="p-2 rounded-xl bg-red-500 active:bg-red-600 text-white transition-all touch-target shadow-[0_2px_0_rgb(153,27,27)] btn-bouncy"
          title="Arreter le chronometre"
        >
          <Square size={14} />
        </button>
      )}

      {finished && (
        <button
          data-testid="timer-reset-btn"
          onClick={handleReset}
          className="p-2 rounded-xl bg-slate-200 active:bg-slate-300 text-slate-600 transition-all touch-target"
          title="Recommencer"
        >
          <RotateCcw size={14} />
        </button>
      )}
    </div>
  );
}
